import type { ReactNode } from 'react';

import logoContrato from '@/assets/logo-contrato.png';
import logoContratoEscuro from '@/assets/logo-contrato-escuro.png';
import simboloContrato from '@/assets/simbolo-contrato.png';
import simboloContratoEscuro from '@/assets/simbolo-contrato-escuro.png';

/**
 * A marca da Contrato Terraplanagem, e a co-marca com a Atenas (p.04 e p.24).
 *
 * POR QUE A MARCA E IMAGEM E NAO TEXTO
 * A pagina 04 do manual e categorica sobre a assinatura CONTRATO: "Grotesca
 * pesada em laranja, entre duas barras pretas. NUNCA redesenhar ou substituir
 * por fonte de sistema." Escrever "Contrato" em Chakra Petch e exatamente a
 * substituicao que a regra proibe — por mais parecida que a fonte seja.
 *
 * POR QUE ELA MORA NA NAVEGACAO E NAO NA BARRA SUPERIOR
 * O tamanho minimo em tela e 96 px de largura (p.04). Na proporcao 550x453 isso
 * da 79 px de altura, e a barra superior tem 64 px fixos (p.13) — o logotipo
 * completo simplesmente nao cabe la sem violar o minimo. A navegacao tem 240 px
 * de largura e sobra vertical, entao e onde o logotipo cabe inteiro e legivel.
 * E onde as telas de referencia tambem o colocam.
 *
 * ABAIXO DE 1280 PX (R01) a navegacao recolhe para 72 px e o logotipo completo deixa
 * de caber. Ai vale a unica excecao que o manual permite: o SIMBOLO ISOLADO,
 * minimo 32 px (p.04). Ele aparece em 40 px, acima do piso.
 *
 * O FUNDO DO ARQUIVO
 * O PNG entregue tinha o branco pintado nos pixels (RGBA, mas alfa 255 em toda
 * a imagem). O fundo foi recortado por preenchimento a partir das bordas, nao
 * por remocao global de branco — senao os brancos INTERNOS do desenho (vidro da
 * cabine, vaos da esteira) desapareceriam junto. Continua sendo paliativo: o
 * vetor (AI, EPS ou SVG) e uma das tres pendencias que o proprio manual lista
 * na p.25, e sem ele nao ha impressao nem sinalizacao.
 *
 * NO TEMA ESCURO, OUTRO ARQUIVO (p.05 e p.21: "logo: trocar o arquivo, nao
 * aplicar filtro"). A versao "fundo escuro" da p.05 — preto vira branco,
 * laranja permanece — foi derivada do mesmo PNG, como o proprio manual fez
 * ("as versoes acima foram derivadas do PNG fornecido"): os neutros invertem
 * a luminosidade, o laranja fica. Sem ela, "TERRAPLANAGEM" e as duas barras
 * pretas sumiam no fundo escuro. O vetor continua pendente (p.25).
 */

/** As duas versoes do mesmo desenho; o tema escolhe qual aparece. */
export function PorTema({ claro, escuro }: { readonly claro: ReactNode; readonly escuro: ReactNode }) {
  return (
    <>
      <span className="contents escuro:hidden">{claro}</span>
      <span className="hidden escuro:contents">{escuro}</span>
    </>
  );
}

export function Marca() {
  /* Topo do menu (p.34): o logotipo e, a direita, a assinatura da Atenas, com
     o divisor de 1 px (p.61). O logotipo vai a 56 px de altura — acima do
     minimo de 48 px da p.09 —, e a assinatura fica abaixo dos 60% da altura
     dele, que e o teto da co-marca. */
  const logo = (arquivo: string) => (
    <img
      src={arquivo}
      alt="Contrato Terraplanagem"
      width={68}
      height={56}
      className="h-14 w-auto max-[1280px]:hidden group-data-[gaveta=aberta]/nav:block!"
    />
  );
  /* Trilho de icones: o simbolo isolado, 40 px (minimo do manual: 24, p.09). */
  const simbolo = (arquivo: string) => (
    <img
      src={arquivo}
      alt="Contrato Terraplanagem"
      width={40}
      height={40}
      className="mx-auto hidden h-10 w-10 max-[1280px]:block group-data-[gaveta=aberta]/nav:hidden!"
    />
  );
  return (
    <div className="flex items-center gap-3 px-2 pt-1 max-[1280px]:justify-center max-[1280px]:px-0 group-data-[gaveta=aberta]/nav:justify-start! group-data-[gaveta=aberta]/nav:px-2!">
      <PorTema claro={logo(logoContrato)} escuro={logo(logoContratoEscuro)} />
      <PorTema claro={simbolo(simboloContrato)} escuro={simbolo(simboloContratoEscuro)} />
      <span aria-hidden="true" className="h-8 w-px shrink-0 bg-borda max-[1280px]:hidden group-data-[gaveta=aberta]/nav:block!" />
      <AssinaturaAtenas className="max-[1280px]:hidden group-data-[gaveta=aberta]/nav:flex!" />
    </div>
  );
}

/**
 * O simbolo isolado na barra superior, abaixo de 1024 px (p.04: minimo 32 px).
 *
 * Ali o menu esta recolhido na gaveta, com o logotipo dentro, e o topo ficava
 * sem marca nenhuma — a p.24 manda o topo do sistema levar a Contrato.
 */
export function SimboloContrato({ className = '' }: { readonly className?: string }) {
  const simbolo = (arquivo: string) => (
    <img src={arquivo} alt="Contrato Terraplanagem" width={32} height={32} className={`size-8 shrink-0 ${className}`} />
  );
  return <PorTema claro={simbolo(simboloContrato)} escuro={simbolo(simboloContratoEscuro)} />;
}

/**
 * A assinatura da Atenas (v2.1, p.61): "POWERED BY" pequeno e o nome no azul
 * da Atenas — o unico lugar, alem do papel de consulta, onde esse azul entra.
 * A Contrato domina: a assinatura fica a direita, depois do divisor, e no
 * maximo a 60% da altura do logotipo.
 *
 * A p.61 manda usar o arquivo oficial da Atenas nas telas; ele ainda nao foi
 * entregue, e ate la a assinatura e composta em texto, como o proprio manual
 * a desenha. O "Powered by" fica em 11 px, o piso da escala (p.15).
 */
export function AssinaturaAtenas({ className = '' }: { readonly className?: string }) {
  return (
    /* `txt-rotulo`: o sobretitulo da p.15 (11/600, caixa alta), o menor nivel
       da escala — a assinatura nunca compete com o logotipo. */
    <span className={`flex shrink-0 flex-col gap-1 whitespace-nowrap leading-none ${className}`}>
      <span className="txt-rotulo text-tinta-apagada">Powered by</span>
      <span className="txt-rotulo text-consulta">Atenas</span>
    </span>
  );
}

/**
 * A co-marca do login (p.24).
 *
 * "Topo do sistema e login levam as duas." Sao as UNICAS duas telas onde a
 * Contrato e a Atenas aparecem juntas — relatorio para cliente e documento
 * contratual levam so a Contrato; suporte e release notes, so a Atenas.
 *
 * Aqui as duas ficam lado a lado de verdade, e nao em cantos opostos como na
 * barra superior. O manual da a construcao exata: separador de 1 px com 16 px
 * de cada lado, e a Atenas com no maximo 60% da altura do logo Contrato. O
 * logotipo vai a 160 px de largura, o que na proporcao 550x453 do arquivo da
 * 132 px de altura; o teto da Atenas e portanto 79 px, e ela ocupa duas linhas
 * de 16 px. A inversao de hierarquia que o manual corrige nas telas atuais
 * ("Powered by Atenas" maior que o logo Contrato) nao tem como acontecer.
 *
 * O "Powered by" fica em 11 px e nao nos 8,5 px que a p.24 pede, pelo mesmo
 * motivo da barra superior: a p.12 fixa que nenhum texto da interface desce
 * abaixo de 11 px, e entre duas regras do mesmo documento vale a que protege o
 * leitor.
 *
 * A COR DO "POWERED BY" E `--text-secondary`, E NAO `--text-muted`
 * A gemea da barra superior (`AssinaturaAtenas`) usa `--text-muted`, e escapa
 * porque o header e `bg-card`: branco, 4,62:1. Aqui o mesmo texto cai sobre
 * `bg-tela` (`--surface-canvas`), onde `--text-muted` mede 4,15:1 — abaixo da
 * regra 7 do checklist (p.25). A conta ja esta registrada em
 * `CabecalhoPagina.tsx` e em `Navegacao.tsx`, e a escolha aqui e a mesma que o
 * sobretitulo fez la: texto institucional de 11 px nao e elemento
 * desabilitado e nao tem isencao de contraste.
 */
export function CoMarcaLogin() {
  const logo = (arquivo: string) => (
    <img src={arquivo} alt="Contrato Terraplanagem" width={160} height={132} className="h-33 w-40" />
  );
  /* Separador e Atenas andam JUNTOS: se a largura nao comporta a assinatura
     ao lado do logo, ela desce inteira para baixo dele, e "Powered by" e
     "Atenas" nunca quebram palavra por palavra. */
  return (
    <div className="flex flex-wrap items-center gap-y-4">
      <PorTema claro={logo(logoContrato)} escuro={logo(logoContratoEscuro)} />
      <span className="flex items-center whitespace-nowrap">
        <span className="ml-4 h-16 w-px shrink-0 bg-borda" aria-hidden="true" />
        <AssinaturaAtenas className="ml-4" />
      </span>
    </div>
  );
}
